import { useEffect, useState } from "react";
import styled from "styled-components";
import { Modal, message } from "antd";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { HolderOutlined } from "@ant-design/icons";

import { PostCase, postCaseDisplayName } from "../../../api/postCaseAPI";

interface Props {
  open: boolean;
  cases: PostCase[];
  onCancel: () => void;
  onSubmit: (ordered: PostCase[]) => Promise<void>;
}

const PostCaseOrderModal = ({ open, cases, onCancel, onSubmit }: Props) => {
  const [ordered, setOrdered] = useState<PostCase[]>(cases);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setOrdered(cases);
  }, [open, cases]);

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    const next = ordered.slice();
    const [moved] = next.splice(result.source.index, 1);
    next.splice(result.destination.index, 0, moved);
    setOrdered(next);
  };

  // 순서가 실제로 바뀌었는지 (안 바뀌었으면 저장 버튼 비활성)
  const isDirty = ordered.some((pc, i) => pc.postCaseId !== cases[i]?.postCaseId);

  const handleOk = async () => {
    if (!isDirty) {
      onCancel();
      return;
    }
    setSaving(true);
    try {
      await onSubmit(ordered);
    } catch (e: any) {
      message.error(e?.message || "순서 변경에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Case 순서 변경"
      okText="저장"
      cancelText="취소"
      okButtonProps={{ disabled: !isDirty }}
      confirmLoading={saving}
      onOk={handleOk}
      onCancel={onCancel}
      centered
      destroyOnClose
      width={440}
    >
      <Desc>드래그해서 순서를 바꾸세요. 이름이 없는 Case 의 번호는 순서대로 다시 매겨집니다.</Desc>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="postCaseOrder">
          {(provided) => (
            <List ref={provided.innerRef} {...provided.droppableProps}>
              {ordered.map((pc, idx) => (
                <Draggable
                  key={pc.postCaseId}
                  draggableId={String(pc.postCaseId)}
                  index={idx}
                >
                  {(dp, snapshot) => {
                    const subInfo = [pc.gender, pc.age].filter(Boolean).join(" / ");
                    return (
                      <Row
                        ref={dp.innerRef}
                        {...dp.draggableProps}
                        {...dp.dragHandleProps}
                        $dragging={snapshot.isDragging}
                        style={dp.draggableProps.style}
                      >
                        <Holder>
                          <HolderOutlined />
                        </Holder>
                        <Order>{postCaseDisplayName(pc, idx)}</Order>
                        <Name title={pc.organ || ""}>{pc.organ || "-"}</Name>
                        {subInfo && <Sub>{subInfo}</Sub>}
                      </Row>
                    );
                  }}
                </Draggable>
              ))}
              {provided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>
    </Modal>
  );
};

const Desc = styled.div`
  margin-bottom: 12px;
  font-size: 13px;
  color: #595959;
  line-height: 1.6;
`;

const List = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  max-height: 52vh;
  overflow-y: auto;
`;

const Row = styled.div<{ $dragging: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  background: ${({ $dragging }) => ($dragging ? "#e6f4ff" : "#fff")};
  border-bottom: 1px solid #f5f5f5;
  cursor: grab;

  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: ${({ $dragging }) => ($dragging ? "#e6f4ff" : "#fafafa")};
  }
`;

const Holder = styled.span`
  color: #bfbfbf;
  display: inline-flex;
  flex-shrink: 0;
`;

const Order = styled.span`
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  color: #16202b;
  min-width: 58px;
`;

const Name = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: #595959;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Sub = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  color: #8c8c8c;
`;

export default PostCaseOrderModal;
